// =============================================================================
// Repository: User status data — fetches all focus/absence events for the org
// Used by UserStatusContext to compute presence for ALL members
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

export interface StatusEvent {
  id: string;
  event_type: string;
  start_at: string | null;
  end_at: string;
  created_by: string;
}

export interface StatusParticipant {
  org_member_id: string;
  rsvp_status: string;
  event_id: string;
}

/**
 * Fetch all focus/absence events for an org (lightweight — only status-relevant fields).
 */
export async function fetchOrgStatusEvents(orgId: string): Promise<StatusEvent[]> {
  const { data, error } = await supabase.rpc('get_org_status_events', {
    p_org_id: orgId,
  });

  if (error) {
    console.error('[statusRepository] fetchOrgStatusEvents error:', error);
    return [];
  }
  return (data || []) as StatusEvent[];
}

/**
 * Fetch participants for given event IDs (only accepted ones matter for status).
 */
export async function fetchStatusParticipants(orgId: string, eventIds: string[]): Promise<StatusParticipant[]> {
  if (eventIds.length === 0) return [];

  const { data, error } = await supabase.rpc('get_status_event_participants', {
    p_org_id: orgId,
    p_event_ids: eventIds,
  });

  if (error) {
    console.error('[statusRepository] fetchStatusParticipants error:', error);
    return [];
  }
  return (data || []) as StatusParticipant[];
}

/**
 * Fetch all active org member IDs.
 */
export async function fetchOrgMemberIds(orgId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('core_organization_members')
    .select('id')
    .eq('org_id', orgId)
    .eq('status', 'active');

  if (error) {
    console.error('[statusRepository] fetchOrgMemberIds error:', error);
    return [];
  }
  return (data || []).map(d => d.id);
}
